import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Clock, 
    Play, 
    Square, 
    Users, 
    AlertTriangle,
    Filter,
    Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, formatDistanceToNow, differenceInHours } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendanceCard = ({ record, onClockOut, isOwnRecord }) => {
    const hoursActive = record.clock_in 
        ? differenceInHours(new Date(), new Date(record.clock_in))
        : 0;
    const isFatigued = hoursActive >= 8;
    
    return (
        <Card className={`${isFatigued ? 'border-l-4 border-l-amber-500' : ''}`}>
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <Avatar className="h-12 w-12">
                                <AvatarFallback className="bg-primary text-white">
                                    {record.user_name?.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                            </Avatar>
                            {!record.clock_out && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white status-pulse"></div>
                            )}
                        </div>
                        <div>
                            <h4 className="font-medium">{record.user_name}</h4>
                            <p className="text-sm text-muted-foreground">
                                {record.activity_name || 'General Duty'}
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-sm text-muted-foreground">
                                Clocked in {formatDistanceToNow(new Date(record.clock_in), { addSuffix: true })}
                            </p>
                            {record.clock_out ? (
                                <p className="font-medium">{record.duration_hours?.toFixed(1)} hours</p>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <p className="font-medium">{hoursActive}+ hours active</p>
                                    {isFatigued && (
                                        <Badge variant="outline" className="text-amber-600 border-amber-400">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            Fatigue Alert
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {!record.clock_out && isOwnRecord && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => onClockOut(record.id)}
                                data-testid={`clock-out-${record.id}`}
                            >
                                <Square className="w-4 h-4 mr-2" />
                                Clock Out
                            </Button>
                        )}
                        
                        {record.clock_out && (
                            <Badge variant="secondary">Completed</Badge>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default function AttendancePage() {
    const { user } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [activeMembers, setActiveMembers] = useState([]);
    const [myAttendance, setMyAttendance] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clockLoading, setClockLoading] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState('none');
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            const [attendanceRes, activeRes, myActiveRes, activitiesRes] = await Promise.all([
                axios.get(`${API_URL}/attendance`),
                axios.get(`${API_URL}/attendance/active`),
                axios.get(`${API_URL}/attendance/my-active`),
                axios.get(`${API_URL}/activities`)
            ]);
            
            setAttendance(attendanceRes.data);
            setActiveMembers(activeRes.data);
            setMyAttendance(myActiveRes.data);
            setActivities(activitiesRes.data);
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };
    
    const handleClockIn = async () => {
        setClockLoading(true);
        try {
            const activityId = selectedActivity !== 'none' ? selectedActivity : null;
            const response = await axios.post(`${API_URL}/attendance/clock-in`, null, {
                params: { activity_id: activityId }
            });
            setMyAttendance(response.data);
            toast.success('Clocked in successfully!');
            fetchData();
        } catch (error) {
            toast.error('Failed to clock in');
        } finally {
            setClockLoading(false);
        }
    };
    
    const handleClockOut = async (attendanceId) => {
        setClockLoading(true);
        try {
            await axios.post(`${API_URL}/attendance/clock-out/${attendanceId}`);
            setMyAttendance(null);
            toast.success('Clocked out successfully!');
            fetchData();
        } catch (error) {
            toast.error('Failed to clock out');
        } finally {
            setClockLoading(false);
        }
    };
    
    const filteredAttendance = attendance.filter(record => {
        const matchesSearch = record.user_name?.toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === 'all' || 
            (filter === 'active' && !record.clock_out) ||
            (filter === 'completed' && record.clock_out);
        return matchesSearch && matchesFilter;
    });
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-6">
                                <div className="skeleton h-4 w-24 mb-2 rounded"></div>
                                <div className="skeleton h-8 w-16 rounded"></div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }
    
    const fatigueCount = activeMembers.filter(m => {
        const hours = differenceInHours(new Date(), new Date(m.clock_in));
        return hours >= 8;
    }).length;
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
            {/* Clock In/Out Section */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-bold">Your Status</h2>
                            {myAttendance ? (
                                <p className="text-muted-foreground mt-1">
                                    You've been active for {formatDistanceToNow(new Date(myAttendance.clock_in))}
                                    {myAttendance.activity_name && ` - ${myAttendance.activity_name}`}
                                </p>
                            ) : (
                                <p className="text-muted-foreground mt-1">
                                    You're not currently clocked in
                                </p>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-3">
                            {!myAttendance && (
                                <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                                    <SelectTrigger className="w-48" data-testid="select-activity">
                                        <SelectValue placeholder="Select activity (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No specific activity</SelectItem>
                                        {activities.map((activity) => (
                                            <SelectItem key={activity.id} value={activity.id}>
                                                {activity.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            
                            {myAttendance ? (
                                <Button
                                    size="lg"
                                    variant="destructive"
                                    onClick={() => handleClockOut(myAttendance.id)}
                                    disabled={clockLoading}
                                    className="clock-btn gap-2"
                                    data-testid="clock-out-btn"
                                >
                                    <Square className="w-5 h-5" />
                                    Clock Out
                                </Button>
                            ) : (
                                <Button
                                    size="lg"
                                    onClick={handleClockIn}
                                    disabled={clockLoading}
                                    className="ses-btn-primary clock-btn gap-2"
                                    data-testid="clock-in-btn"
                                >
                                    <Play className="w-5 h-5" />
                                    Clock In
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30">
                                <Users className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Active Now</p>
                                <p className="text-2xl font-bold">{activeMembers.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-primary/10">
                                <Clock className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Records Today</p>
                                <p className="text-2xl font-bold">{attendance.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className={fatigueCount > 0 ? 'border-amber-400' : ''}>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${fatigueCount > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-muted'}`}>
                                <AlertTriangle className={`w-6 h-6 ${fatigueCount > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Fatigue Alerts</p>
                                <p className="text-2xl font-bold">{fatigueCount}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-attendance"
                            />
                        </div>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Records</SelectItem>
                                <SelectItem value="active">Active Only</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
            
            {/* Attendance List */}
            <div className="space-y-3">
                <h3 className="font-semibold">Attendance Records</h3>
                {filteredAttendance.length > 0 ? (
                    filteredAttendance.map((record) => (
                        <AttendanceCard
                            key={record.id}
                            record={record}
                            onClockOut={handleClockOut}
                            isOwnRecord={record.user_id === user?.id}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                            <h3 className="font-medium text-lg">No attendance records</h3>
                            <p className="text-muted-foreground mt-1">
                                {search || filter !== 'all'
                                    ? 'Try adjusting your filters'
                                    : 'Clock in to start tracking your attendance'}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
